'use client';

import { useEffect, useState } from 'react';
import { signIn, getSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { useErrorHandler } from '@/hooks/use-error-handler';
import { PageHeader } from '@/components/layout/page-header';
import { ErrorDisplay } from '@/components/common/error-display';

export function SignInForm() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const router = useRouter();
  const { errorState, handleError, clearError } = useErrorHandler();

  // Server-rendered controls must not accept input before their handlers exist.
  useEffect(() => { setIsHydrated(true); }, []);
  const controlsDisabled = !isHydrated || isLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    clearError();

    try {
      const result = await signIn('credentials', {
        email,
        password,
        redirect: false,
      });

      if (result?.error) {
        handleError(new Error('Invalid email or password'), 'Sign-in authentication');
        setIsLoading(false);
      } else {
        // Get the session to check user role
        const session = await getSession();
        if (session) {
          // Navigate - keep loading active until navigation completes
          router.push('/dashboard');
          // Don't set isLoading to false here - let the page navigation handle it
          // The loading will naturally stop when the component unmounts during navigation
        } else {
          handleError(new Error('Session creation failed'), 'Sign-in session');
          setIsLoading(false);
        }
      }
    } catch (error) {
      handleError(error, 'Sign-in process');
      setIsLoading(false);
    }
  };

  return <main className="flex min-h-dvh items-center justify-center bg-background p-4 text-foreground sm:p-6">
    <section className="w-full max-w-[400px] rounded-lg border bg-card p-5 sm:p-6">
      <p className="mb-5 text-[13px] text-muted-foreground">VoodPortal</p>
      <PageHeader title="Sign in" />
      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        {errorState.hasError ? <ErrorDisplay error={errorState.errorMessage} variant={errorState.isValidationWarning ? 'warning' : 'destructive'} showClearButton={false} /> : null}
        <div className="flex flex-col gap-2"><Label htmlFor="email">Email Address</Label><Input id="email" type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required disabled={controlsDisabled} /></div>
        <div className="flex flex-col gap-2"><Label htmlFor="password">Password</Label><div className="relative">
          <Input id="password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required disabled={controlsDisabled} className="pr-12" />
          <Button type="button" variant="ghost" size="icon" aria-label={showPassword ? 'Hide password' : 'Show password'} aria-pressed={showPassword} className="absolute right-0 top-0 h-full" onClick={() => setShowPassword(!showPassword)} disabled={controlsDisabled}>{showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}</Button>
        </div></div>
        <Button type="submit" disabled={controlsDisabled} aria-busy={isLoading || undefined}>{isLoading ? <><Loader2 className="animate-spin" aria-hidden="true" />Signing in...</> : 'Sign In'}</Button>
      </form>
    </section>
  </main>;
}
